import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Heading,
  useColorModeValue,
  VStack,
  Text,
  Spinner,
  Box,
  Divider,
  HStack,
} from '@chakra-ui/react';
import { useContext, useState, useEffect } from 'react';
import { useMfa } from '../../context/MfaContext';
import { MeContext } from '../../context/MeContext';
import { useMfaApi } from '../../hooks/useMfaApi';
import { useMfaToast } from '../../hooks/useMfaToast';
import MfaSetUpTotp from '../client/account/MfaSetUpTotp';
import MfaSetUpEmail from '../client/account/MfaSetUpEmail';
import MfaRecoveryCode from '../client/account/MfaRecoveryCode';
import MfaSetUp from '../client/account/MfaSetUp';

const MfaOperationModal = ({
  isOpen,
  onClose,
  operation,
  currentMethod = null,
}) => {
  const meCtx = useContext(MeContext).state;
  const { fetchData } = useContext(MeContext);
  const {
    startMfaSetup,
    clearMfaOperation,
    startMfaOperation,
    completeMfaOperation,
  } = useMfa();
  const {
    setupMfa,
    resetAuthenticator,
    regenerateRecoveryCodes,
    revertOtpSetup,
  } = useMfaApi();
  const { showSuccessToast, showErrorToast } = useMfaToast();

  const modeText = useColorModeValue('secondaryDark08', '#DDDDDD');

  const [step, setStep] = useState('selection');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setStep('selection');
      setSelectedMethod(null);
      setIsLoading(false);
      setRecoveryCodes([]);
    }
  }, [isOpen]);

  const getModalTitle = () => {
    switch (operation) {
      case 'enable':
        return 'Enable MFA';
      case 'change-method':
        return 'Change OTP Method';
      case 'reset':
        return 'Reset Authenticator';
      case 'regenerate-codes':
        return 'Regenerate Recovery Codes';
      default:
        return 'MFA Operation';
    }
  };

  const handleMethodSelect = async (method) => {
    setSelectedMethod(method);
    startMfaOperation();
    setIsLoading(true);

    try {
      const data = await setupMfa(method);
      startMfaSetup(method, data.otp_uri, data.otp_secret);
      setStep('setup');
    } catch (error) {
      showErrorToast(
        'Error setting up MFA',
        error.response?.data?.message ||
          'Failed to set up multi-factor authentication. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleMethodChangeSuccess = () => {
    fetchData();
    completeMfaOperation();
    onClose();
    showSuccessToast(
      'MFA method updated successfully',
      'Your multi-factor authentication method has been changed'
    );
  };

  const handleResetAuthenticator = async () => {
    setIsLoading(true);
    startMfaOperation();
    try {
      const data = await resetAuthenticator();
      startMfaSetup('totp', data.otp_uri, data.otp_secret);
      setStep('setup');
    } catch (error) {
      showErrorToast(
        'Error resetting authenticator',
        error.response?.data?.message ||
          'Failed to reset authenticator. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetAuthenticatorComplete = () => {
    completeMfaOperation();
    fetchData();
    showSuccessToast(
      'Authenticator reset successfully',
      'Your authenticator app has been reset and updated'
    );
    onClose();
  };

  const handleRegenerateRecoveryCodes = async () => {
    setIsLoading(true);
    try {
      const data = await regenerateRecoveryCodes();
      setRecoveryCodes(data.codes);
      setStep('recovery');
    } catch (error) {
      showErrorToast(
        'Error regenerating recovery codes',
        error.response?.data?.message ||
          'Failed to regenerate recovery codes. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoveryCodesRegenerated = () => {
    fetchData();
    showSuccessToast(
      'Recovery codes regenerated',
      'Your new recovery codes have been generated successfully'
    );
    onClose();
  };

  const handleRecoveryCodesComplete = () => {
    fetchData();
    showSuccessToast(
      'MFA enabled successfully',
      'Your account is now protected with multi-factor authentication'
    );
    onClose();
  };

  const handleCancel = async () => {
    if (step !== 'selection') {
      await revertOtpSetup(currentMethod, meCtx.account.otp_secret).then(() => {
        clearMfaOperation();
        setStep('selection');
        setSelectedMethod(null);
        setIsLoading(false);
        setRecoveryCodes([]);
        onClose();
        fetchData();
      });
    } else {
      clearMfaOperation();
      setStep('selection');
      setSelectedMethod(null);
      setIsLoading(false);
      setRecoveryCodes([]);
      onClose();
    }
  };

  const handleClose = () => {
    handleCancel();
  };

  const renderStep = () => {
    if (isLoading) {
      return (
        <VStack spacing={4} py={8} alignItems="center">
          <Spinner size="lg" color="brand" />
          <Text textStyle="bodySmall" color="#666666">
            Loading...
          </Text>
        </VStack>
      );
    }

    switch (step) {
      case 'selection':
        if (operation === 'regenerate-codes') {
          return (
            <VStack spacing={4} py={4}>
              <Text textStyle="bodyFamilyMedium" color={modeText}>
                Are you sure you want to regenerate your recovery codes?
                <br />
                Your old recovery codes will no longer work.
              </Text>
            </VStack>
          );
        }

        if (operation === 'reset') {
          return (
            <VStack spacing={4} py={4}>
              <Text textStyle="bodyFamilyMedium" color={modeText}>
                Are you sure you want to reset your authenticator?
                <br />
                This will require you to set up MFA again with a new QR code.
              </Text>
            </VStack>
          );
        }

        return (
          <Box>
            <MfaSetUp
              onMethodSelect={handleMethodSelect}
              isModal={true}
              otpMethod={currentMethod}
            />
            <Text textStyle="bodySmall" color="#666666" mt={4}>
              Current method:{' '}
              {currentMethod === 'totp' ? 'Authentication app' : 'Email'}
            </Text>
          </Box>
        );

      case 'setup':
        if (selectedMethod === 'totp' || operation === 'reset') {
          return (
            <MfaSetUpTotp
              isModal={true}
              onComplete={
                operation === 'reset'
                  ? handleResetAuthenticatorComplete
                  : () => setStep('recovery')
              }
            />
          );
        } else if (selectedMethod === 'email') {
          return (
            <MfaSetUpEmail
              onComplete={handleMethodChangeSuccess}
              isModal={true}
            />
          );
        }
        return null;

      case 'recovery':
        return (
          <MfaRecoveryCode
            recoveryCodes={recoveryCodes}
            onComplete={
              operation === 'reset'
                ? handleResetAuthenticatorComplete
                : operation === 'regenerate-codes'
                  ? handleRecoveryCodesRegenerated
                  : handleRecoveryCodesComplete
            }
          />
        );

      default:
        return null;
    }
  };

  const getFooterButtons = () => {
    if (step === 'selection') {
      if (operation === 'regenerate-codes') {
        return (
          <HStack spacing={4} justifyContent="center" width="full">
            <Button onClick={handleCancel} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={handleRegenerateRecoveryCodes}
              isLoading={isLoading}
            >
              Regenerate Codes
            </Button>
          </HStack>
        );
      }

      if (operation === 'reset') {
        return (
          <HStack spacing={4} justifyContent="center" width="full">
            <Button onClick={handleCancel} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleResetAuthenticator} isLoading={isLoading}>
              Reset Authenticator
            </Button>
          </HStack>
        );
      }

      return null;
    }
  };

  return (
    <Modal
      onClose={handleClose}
      isOpen={isOpen}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading
            fontSize={{ base: '24px', lg: '32px' }}
            lineHeight="130%"
            textAlign="center"
          >
            {getModalTitle()}
          </Heading>
          <ModalCloseButton />
        </ModalHeader>
        <Divider />
        <ModalBody>{renderStep()}</ModalBody>
        {getFooterButtons() && <ModalFooter>{getFooterButtons()}</ModalFooter>}
      </ModalContent>
    </Modal>
  );
};

export default MfaOperationModal;
